import type { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import styled, { useTheme } from "styled-components/macro";
import useSWR from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import { PrimaryButtonWithPlusIcon } from "../../../../../components/Buttons/Buttons";
import { DelayedSpinner } from "../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../components/Error";
import { LinkUnstyled } from "../../../../../components/LinkUnstyled/LinkUnstyled";
import { ListingCardGrid } from "../../../../../components/ListingGrid/ListingCardGrid";
import { ListingGridCard } from "../../../../../components/ListingGrid/ListingGridCard";
import { Modal } from "../../../../../components/Modal/Modal";
import { Pagination } from "../../../../../components/Pagination/Pagination";
import { SearchBar } from "../../../../../components/SearchBar/SearchBar";
import { NoData } from "../../../../../components/Table/Table";
import { NavBarTabs } from "../../../../../layout/shared/Nav/NavBar";
import type {
  TemplateLocationState,
  UUID,
  WithPagination,
} from "../../../../../types/types";
import { PageWrapper } from "../../../../../layout/portalPageLayout";
import type {
  AttributeTemplateSummarySchema,
  TemplateType,
} from "../../../../../types/types.PIM";
import { useDebounce } from "../../../../../util/hooks";
import { useRoutePath } from "../../../../../util/Routing";
import { useStoreState } from "../../../../../util/util";
import {
  CreateNewTemplateForm,
  TEMPLATE_TYPE_TOGGLE_MAP,
} from "./CreateNewTemplateForm";
import { useAuthContext } from "../../../../../components/Auth";
import { TemplatesNavWithPadding } from "./TemplatesNav";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import type { EntityType } from "../../../../../types/types.MDM";
import { spacingTokens } from "../../../../../designTokens";

const LIMIT = 20;

//TODO this is temporary do not commit this, create shared file for "Table Page components"
const SearchBarAndFilters = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const RightSideButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_3xl")};
  align-items: flex-end;
  margin-left: 10px;
`;

const constructQuery = ({
  baseUrl,
  query,
  templateType,
  offset,
  limit,
}: {
  baseUrl: string;
  query: string;
  templateType: TemplateType;
  offset: number;
  limit: number;
}) => {
  const params = new URLSearchParams();
  if (query) params.append("q", query);
  params.append("template_type", templateType);
  params.append("limit", String(limit));
  params.append("offset", String(offset));
  return baseUrl + params;
};

const templateTypeTabs: TemplateType[] = [
  "product",
  "customer",
  "supplier",
  "distributor",
];

function isTemplateType(value?: string | null): value is TemplateType {
  return (
    value === "product" ||
    value === "customer" ||
    value === "supplier" ||
    value === "distributor"
  );
}

export function SellerAdminTemplateListingPage() {
  const [query, setQuery] = useQueryParams({
    q: StringParam,
    template_type: StringParam,
  });
  const { tenant_id, storefront_metadata } = useStoreState();
  const { t } = useTranslation();
  const { getLabel, getTemplateDisplayName } = useMdmEntityDisplayNames();
  const [searchQuery, setSearchQuery] = useState(query.q || "");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [showCreateTemplateForm, setShowCreateTemplateForm] = useState(false);
  const [offset, setOffset] = useState(0);

  const theme = useTheme();

  const visibleTemplateTabs = templateTypeTabs.filter((type) => {
    const toggle = TEMPLATE_TYPE_TOGGLE_MAP[type];
    return toggle === null || storefront_metadata[toggle];
  });
  const history = useHistory();
  const location = useLocation();
  const { adminPath } = useRoutePath();
  const { hasPermission } = useAuthContext();
  const selectedTemplateType = isTemplateType(query.template_type)
    ? query.template_type
    : "product";
  const tabIndex = Math.max(
    0,
    visibleTemplateTabs.indexOf(selectedTemplateType)
  );

  const {
    data: templates,
    error: templatesError,
    mutate: mutateTemplates,
  } = useSWR<
    WithPagination<{ data: AttributeTemplateSummarySchema[] }>,
    AxiosError
  >(
    constructQuery({
      baseUrl: `/v2/tenants/${tenant_id}/pim/templates/summary?`,
      query: debouncedSearchQuery,
      templateType: selectedTemplateType,
      offset,
      limit: LIMIT,
    })
  );

  const isLoadingTemplates = !templates && !templatesError;

  // Restore state when returning to the page
  useEffect(() => {
    const state = location.state as TemplateLocationState;
    if (!state) {
      return;
    }

    const nextQuery: { q?: string; template_type?: TemplateType } = {};

    if (state.searchQuery) {
      setSearchQuery(state.searchQuery);
      nextQuery.q = state.searchQuery;
    }
    if (state.templateType) {
      nextQuery.template_type = state.templateType;
    }

    if (nextQuery.q || nextQuery.template_type) {
      setQuery(nextQuery);
    }
  }, [location.state, setQuery]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setOffset(0);
    setQuery({ q: e.target.value, template_type: selectedTemplateType });
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setOffset(0);
    setQuery({ q: undefined, template_type: selectedTemplateType });
  };

  const handleTabChange = (index: number) => {
    const nextType = visibleTemplateTabs[index] || "product";
    setOffset(0);
    setQuery({ q: searchQuery || undefined, template_type: nextType });
  };

  const pageCount = templates
    ? Math.ceil(templates.pagination.total / LIMIT)
    : 0;

  const pageIndex = Math.floor((templates?.pagination.offset ?? 0) / LIMIT) + 1;

  const handlePageClick = (newOffset: number) => {
    setOffset(newOffset);
  };

  if (isLoadingTemplates) {
    return <DelayedSpinner />;
  }

  if (templatesError) {
    return (
      <ErrorPlaceholder
        message={t("There was an error loading the templates")}
      />
    );
  }

  return (
    <PageWrapper>
      <TemplatesNavWithPadding
        style={{ marginBottom: theme.resolve("spacing_xl") }}
      >
        <NavBarTabs
          handleTabsChange={handleTabChange}
          pageTitle={t("Templates")}
          tabs={visibleTemplateTabs.map((templateType) =>
            templateType === "product"
              ? t("Products")
              : getLabel(templateType as EntityType, "plural")
          )}
          tabIndex={tabIndex}
        />
      </TemplatesNavWithPadding>
      <SearchBarAndFilters
        style={{
          marginBottom: theme.resolve("spacing_3xl"),
          padding: `0 ${theme.resolve("spacing_xl")}`,
        }}
      >
        <SearchBar
          query={searchQuery}
          placeHolder={t("Search by template")}
          handleChange={handleSearch}
          handleClearInput={handleClearSearch}
        />
        <RightSideButtonContainer>
          {hasPermission("modify_templates") && (
            <PrimaryButtonWithPlusIcon
              onClick={() => setShowCreateTemplateForm(true)}
              useV2
            >
              {t("Create")}
            </PrimaryButtonWithPlusIcon>
          )}
          <Modal
            overlay
            show={showCreateTemplateForm}
            closeModal={() => setShowCreateTemplateForm(false)}
          >
            <CreateNewTemplateForm
              templateType={selectedTemplateType}
              onSuccess={(id: UUID) => {
                setShowCreateTemplateForm(false);
                mutateTemplates();
                history.push(`${adminPath}/pim/templates/${id}/groups`);
              }}
            />
          </Modal>
        </RightSideButtonContainer>
      </SearchBarAndFilters>
      {templates && templates.data.length === 0 && (
        <NoData style={{ margin: `0 ${theme.resolve("spacing_xl")}` }}>
          {searchQuery ? t("No results found") : t("No templates found")}
        </NoData>
      )}
      {templates && (
        <ListingCardGrid
          style={{ padding: `0 ${theme.resolve("spacing_xl")}` }}
        >
          {templates.data.map((template) => {
            return (
              <LinkUnstyled
                key={template.template_id}
                to={{
                  pathname: `${adminPath}/pim/templates/${template.template_id}/groups`,
                  state: {
                    searchQuery,
                    templateType: selectedTemplateType,
                  } as TemplateLocationState,
                }}
              >
                <ListingGridCard
                  key={template.template_id}
                  firstLineBold={getTemplateDisplayName(template)}
                />
              </LinkUnstyled>
            );
          })}
        </ListingCardGrid>
      )}
      {templates && pageCount > 1 && (
        <div style={{ margin: spacingTokens.spacing_xl }}>
          <Pagination
            pagination={{
              pageCount,
              pageIndex,
              perPage: LIMIT,
            }}
            offset={offset}
            handlePageClick={handlePageClick}
          />
        </div>
      )}
    </PageWrapper>
  );
}
